<template>
    <div>
        <card-header title="Copy Flight" icon="fa-copy"/>

        <card-body v-if="flight">
            <card-list>
                <flight-list-item :flight="flight" :card="card" show-details/>
                <subheader-list-item title="Copy to" icon="fa-copy"/>
                <container-list-item padded>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Trip" label-position="inside">
                                <b-select expanded v-model="tripId">
                                    <option :value="t.id" v-for="t in trips">{{ t.title }} <span v-if="t.id === registration.trip_id">(This Trip)</span></option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Registrations" label-position="inside">
                                <b-taginput
                                    v-model="selectedRegs"
                                    :data="filteredRegistrations"
                                    autocomplete
                                    open-on-focus
                                    field="_label"
                                    icon="search"
                                    placeholder="Add a registration..."
                                    @typing="regFilter = $event">
                                    <template slot="empty">No results.</template>
                                </b-taginput>
                            </b-field>
                            <p class="is-size-7 mt-1">
                                <a @click="selectAll">Add all ({{ registrations.length }})</a>
                                <a class="ml-3" @click="selectedRegs = []" v-if="selectedRegs.length">Clear</a>
                            </p>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-switch :true-value="true" :false-value="false" v-model="link" :disabled="selectedRegIds.length > 1">
                                Link copies to this flight &mdash; changes to the flight details update all linked flights
                            </b-switch>
                            <p class="is-size-7 has-text-grey mt-1" v-if="flight.flight_group_id && link">
                                This flight is already linked to {{ groupSize }} flights; new copies join that group. Registrations already in the group are skipped.
                            </p>
                            <p class="is-size-7 has-text-grey mt-1" v-if="tripId !== registration.trip_id">
                                Copying to another trip: itinerary day links are not carried over.
                            </p>
                        </div>
                    </div>
                </container-list-item>
                <subheader-list-item icon="fa-align-left" title="Flight information"/>
                <container-list-item padded>
                    <div class="columns" v-if="singleTarget">
                        <div class="column">
                            <b-field label="Applies To" label-position="inside">
                                <b-select expanded v-model="flight.applies_to">
                                    <option value="primary">{{singleTarget.user.last_name}}, {{singleTarget.user.first_name}} (Primary)</option>
                                    <option value="secondary" v-if="singleTarget.second_user">{{singleTarget.second_user.last_name}}, {{singleTarget.second_user.first_name}} (Secondary)</option>
                                    <option value="both" v-if="singleTarget.second_user">Both Participants</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns" v-else>
                        <div class="column">
                            <p class="is-size-7 has-text-grey">Applies To is set per registration: both participants for doubles, primary otherwise.</p>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-switch :true-value="1" :false-value="0" v-model="flight.primary_flight">This is an Primary Flight</b-switch>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Class" label-position="inside">
                                <b-select expanded v-model="flight.travel_class">
                                    <option value="first">First</option>
                                    <option value="business">Business</option>
                                    <option value="premium-economy">Premium Economy</option>
                                    <option value="economy">Economy</option>
                                    <option value="tbd">TBD</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Seat Preference" label-position="inside">
                                <b-select expanded v-model="flight.seat_preference">
                                    <option value="window">Window</option>
                                    <option value="aisle">Aisle</option>
                                    <option value="window-adjacent">Window & Adjacent</option>
                                    <option value="aisle-adjacent">Aisle & Adjacent</option>
                                    <option value="none">No Preference</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column is-3">
                            <b-field label="Seat" label-position="inside">
                                <b-input v-model="flight.seat"/>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Flight Cost (Hidden)" label-position="inside">
                                <b-input v-model="flight.cost"></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Reference Number" label-position="inside">
                                <b-input v-model="flight.reference_number"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Additional Flight Notes" label-position="inside">
                                <b-input type="textarea" v-model="flight.flight_notes"></b-input>
                            </b-field>
                        </div>
                    </div>
                </container-list-item>
            </card-list>

        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column has-text-right">
                    <b-button type="is-primary" size="is-small" :disabled="!selectedRegIds.length" @click="submit"><i class="fas fa-copy mr-2"></i>Copy Flight<span v-if="selectedRegIds.length > 1"> ({{ selectedRegIds.length }})</span></b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import {client as http} from '../../http_client';
    import CardHeader from '../../TIER/components/CardHeader';
    import CardBody from '../../TIER/components/CardBody';
    import CardFooter from '../../TIER/components/CardFooter';
    import RegistrationListItem from "../../components/RegistrationListItem";

    import async from 'async';
    import CardList from "../../TIER/components/CardList";
    import CardToolbar from "../../TIER/components/CardToolbar.vue";
    import FlightListItem from "../../components/FlightListItem.vue";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem.vue";
    import ContainerListItem from "../../TIER/components/ContainerListItem.vue";

    export default {
        props: ['card', 'props'],
        components: {ContainerListItem, SubheaderListItem, FlightListItem, CardToolbar, CardList, RegistrationListItem, CardFooter, CardBody, CardHeader},
        data: function() {
            return {
                flight: null,
                trips: [],
                tripId: null,
                registrations: [],
                registration: {trip: {}, user: {}},
                selectedRegs: [],
                regFilter: '',
                link: false
            };
        },
        computed: {
            selectedRegIds: function() {
                return this.selectedRegs.map(r => r.id);
            },
            filteredRegistrations: function() {
                let q = this.regFilter.toLowerCase();
                return this.registrations.filter(r =>
                    !this.selectedRegIds.includes(r.id) &&
                    (!q || r._label.toLowerCase().indexOf(q) >= 0));
            },
            singleTarget: function() {
                return (this.selectedRegs.length === 1) ? this.selectedRegs[0] : null;
            },
            groupSize: function() {
                return (this.flight && this.flight.group_flights) ? this.flight.group_flights.length : 0;
            }
        },
        methods: {
            loadFlight: function() {
                this.$emit('loading', true);

                async.series([
                    // Get flight
                    (cb) => {
                        http.get('/api/registrations/' + this.props.registrationId + '/flights/' + this.props.flightId, {force: true}).then(response => {
                            this.flight = response.data;
                            cb();
                        });
                    },
                    // Get registration
                    (cb) => {
                        http.get('/api/registrations/' + this.props.registrationId, {force: true}).then(response => {
                            this.registration = response.data;
                            this.tripId = this.registration.trip_id;
                            cb();
                        });
                    },
                    // Get active trips for the cross-trip picker
                    (cb) => {
                        http.get('/api/trips/active/0/500', {force: true}).then(response => {
                            this.trips = response.data.trips;
                            // Archived source trip isn't in the active list — add it
                            // so the select shows the default instead of a blank.
                            if (!this.trips.find(t => t.id === this.registration.trip_id))
                                this.trips.unshift(this.registration.trip);
                            cb();
                        });
                    },
                    // Get trip registrations
                    (cb) => {
                        this.loadRegistrations(cb);
                    }
                ], err => {
                    this.$emit('loading', false);
                });
            },
            loadRegistrations: function(cb) {
                http.get('/api/trips/' + this.tripId + '/registrations/0/1000', {force: true}).then(response => {
                    this.registrations = response.data.registrations;
                    this.registrations.sort((a, b) => {
                        return a.user.last_name.localeCompare(b.user.last_name);
                    });
                    this.registrations.forEach(r => {
                        let tag = '';
                        if (r.id === parseInt(this.props.registrationId)) tag = ' (Duplicate)';
                        else if (this.tripId !== this.registration.trip_id && r.user_id === this.registration.user_id) tag = ' (Same Traveller)';
                        r._label = r.user.last_name + ', ' + r.user.first_name + tag;
                    });
                    if (cb) cb();
                });
            },
            selectAll: function() {
                this.selectedRegs = this.registrations.slice();
            },
            submit() {
                this.$emit('loading', true);

                let payload = {
                    ...this.flight,
                    regIds: this.selectedRegIds,
                    link: this.link,
                    applies_to: (this.singleTarget) ? this.flight.applies_to : 'auto'
                };

                http.post('/api/registrations/' + this.props.registrationId + '/flights/' + this.props.flightId + '/copy', payload, {force: true}).then(response => {
                    this.$reloadCard('flights');
                    this.$reloadCard('tasks');
                    this.$reloadCard('task-details', {type: 'flights'});
                    this.$closeCard(this.card);
                }).catch(err => {
                    this.$emit('loading', false);
                });
            }
        },
        watch: {
            tripId: function(newId, oldId) {
                if (oldId === null || newId === oldId) return;
                this.selectedRegs = [];
                this.$emit('loading', true);
                this.loadRegistrations(() => this.$emit('loading', false));
            },
            selectedRegs: function(regs) {
                // Multi-copy is always linked; single copies choose.
                if (regs.length > 1) this.link = true;
            },
            singleTarget: function(target) {
                if (target && !target.second_user && this.flight.applies_to !== 'primary')
                    this.flight.applies_to = 'primary';
            }
        },
        mounted() {
            this.loadFlight();
        }
    };
</script>
